import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FeederContractModel } from '@bo-client/activation-management/feeder-contract/model/feeder-contract.model';
import { PowerGenerationSourceEnum } from '@bo-client/activation-management/grid-registration/model/grid-registration.enum';
import { Observable, Subject } from 'rxjs';
import { HttpHapService } from '@bo-client/core/http/http-hap.service';
import {
  CompensationCategoryConditionEnum,
  CompensationCategoryModel
} from '@bo-client/activation-management/shared/models/compensationCategoryModel';
import { uri } from '@bo-client/shared/constants/uri';
import { FormGroup } from '@angular/forms';
import { ShowCompensationCategoriesSelectionModalComponent } from '@bo-client/activation-management/shared/compensation-categories/show-compensation-categories/show-compensation-categories-selection-modal/show-compensation-categories-selection-modal.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

@Component({
  selector: 'app-show-compensation-categories',
  templateUrl: './show-compensation-categories.component.html',
  styleUrls: ['./show-compensation-categories.component.scss']
})
export class ShowCompensationCategoriesComponent implements OnInit, OnDestroy {
  @Input() inputForm: FormGroup;
  @Input() feederContract: FeederContractModel;
  @Output() compensationCategoryList = new EventEmitter<CompensationCategoryModel[]>();

  public displayedColumns: string[] = ['name', 'powerRange', 'compensationAmount', 'isFullFeed'];
  public compensationCategories: CompensationCategoryModel[] = [];

  private compUnsubs$ = new Subject<void>();

  public totalPower = 0;
  @Input() disable!: boolean;

  @Input()
  hasEditPermission: boolean;

  constructor(private httpHapService: HttpHapService, private dialog: MatDialog) {}

  ngOnInit(): void {
    switch (this.feederContract.powerGenerationSource) {
      case PowerGenerationSourceEnum.PhotoVoltaic: {
        this.totalPower =
          this.feederContract.pvModules.reduce((sum, x) => sum + x.count * x.modulePowerWatt, 0) / 1000;
        break;
      }
      case PowerGenerationSourceEnum.Wind: {
        this.totalPower = this.feederContract.producers.reduce(
          (sum, x) => sum + x.count * x.acActivePowerMax,
          0
        );
      }
    }
    if (this.feederContract.compensationCategories && this.feederContract.compensationCategories.length > 0) {
      this.compensationCategories = this.sortListByPowerRange(this.feederContract.compensationCategories);
    }

    if (this.feederContract.commissioningDate && !this.feederContract.compensationCategories) {
      this.getCompensationCategoriesByDate(
        this.feederContract.commissioningDate,
        this.feederContract.commissioningDate
      );
    }
  }

  getCompensationCategoriesByDate(dateFrom: Date, dateTo: Date) {
    const compCatParameters: CompensationCategoryModel = new CompensationCategoryModel();
    compCatParameters.generationSource = this.feederContract.powerGenerationSource;
    compCatParameters.isFullFeed = this.feederContract.meteringConcept?.isFullFeedIn;
    compCatParameters.validFrom = new Date(dateFrom);
    compCatParameters.validTo = new Date(dateTo);
    this.httpHapService
      .putOrPost(compCatParameters, uri.cmCompCatBySourceAndBetweenMonth)
      .subscribe((answer: CompensationCategoryModel[]) => {
        const filteredList = this.sortListByPowerRange(this.filterPowerLessOrEqual(answer));
        this.feederContract.commissioningDate = this.inputForm.get('commissioningDate').value;
        this.updateCompensationCategories(filteredList);
      });
  }

  handleCalculateCategories() {
    const dateValue = this.inputForm.get('commissioningDate').value;
    this.getCompensationCategoriesByDate(dateValue, dateValue);
  }

  handleRemoveCategory(compensationCategory: CompensationCategoryModel) {
    const updatedCategories = this.compensationCategories.filter(
      (element) => element.id !== compensationCategory.id
    );
    this.updateCompensationCategories(updatedCategories);
  }

  openCategorySelectionModal() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = { preSelectedCategories: this.compensationCategories };
    const dialogRef = this.dialog.open(ShowCompensationCategoriesSelectionModalComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((result: CompensationCategoryModel[]) => {
      const updatedCategories = this.sortListByPowerRange(this.compensationCategories.concat(result));
      this.updateCompensationCategories(updatedCategories);
    });
  }

  sortListByPowerRange(list: CompensationCategoryModel[]) {
    return list.sort((a, b) => (a.powerRange < b.powerRange ? -1 : a.powerRange > b.powerRange ? 1 : 0));
  }

  filterPowerLessOrEqual(list: CompensationCategoryModel[]): CompensationCategoryModel[] {
    const regex = /\d+/g;
    list = list.filter((x) => {
      if (x.powerRange === CompensationCategoryConditionEnum.None) {
        return true;
      }
      const values: string[] = x.powerRange.split('_');
      return +values[1].match(regex) <= this.totalPower;
    });
    return list;
  }

  private updateCompensationCategories(updatedCategories: CompensationCategoryModel[]) {
    this.compensationCategories = updatedCategories;
    this.compensationCategoryList.emit(updatedCategories);
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }
}
